/**
 * FinderWindow Component
 *
 * Main Finder-style window wrapper with macOS chrome.
 * Integrates with MacStudioWindow for dragging/resizing.
 */

import { useState, useCallback, useMemo } from 'react'
import { MacStudioWindow } from '../MacStudioWindow'
import { FinderToolbar } from './FinderToolbar'
import { FinderSplitPane } from './FinderSplitPane'
import { FinderSidebar } from './FinderSidebar'
import { FinderIconView } from './FinderIconView'
import { FinderListView } from './FinderListView'
import './finder.css'

export type ViewMode = 'icon' | 'list' | 'column'

export interface CategoryConfig {
  id: string
  name: string
  icon?: string
  description?: string
}

export interface ContentItem {
  slug: string
  name?: string
  company?: string
  role?: string
  title?: string
  icon?: string
  thumbnail?: string
  category?: string
  tags?: string[]
  content?: string
  [key: string]: unknown
}

export interface FinderWindowProps {
  windowId: string
  title: string
  icon?: string
  initialPath?: string[]
  initialPosition?: { x: number; y: number }

  // Data
  categories: CategoryConfig[]
  items: ContentItem[]
  getCategoryItems?: (categoryId: string) => ContentItem[]

  // Layout
  defaultView?: ViewMode
  showSidebar?: boolean
  showPreview?: boolean
  storageKey?: string

  // Callbacks
  onItemSelect?: (item: ContentItem) => void
  onNavigate?: (path: string[]) => void
}

export function FinderWindow({
  windowId,
  title,
  initialPath = [],
  initialPosition,
  categories,
  items,
  getCategoryItems,
  defaultView = 'icon',
  showSidebar = true,
  showPreview = false,
  storageKey,
  onItemSelect,
  onNavigate,
}: FinderWindowProps) {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>(defaultView)
  const [currentPath, setCurrentPath] = useState<string[]>(initialPath)
  const [selectedCategory, setSelectedCategory] = useState<string>(
    categories[0]?.id || ''
  )
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null)

  // Get items for current category
  const currentItems = useMemo(() => {
    if (getCategoryItems) {
      return getCategoryItems(selectedCategory)
    }
    return items.filter((item) => item.category === selectedCategory)
  }, [items, selectedCategory, getCategoryItems])

  // Handle category selection
  const handleCategorySelect = useCallback(
    (categoryId: string) => {
      setSelectedCategory(categoryId)
      setSelectedItem(null)
      const newPath = [categoryId]
      setCurrentPath(newPath)
      onNavigate?.(newPath)
    },
    [onNavigate]
  )

  // Handle item selection
  const handleItemSelect = useCallback(
    (item: ContentItem) => {
      setSelectedItem(item)
      onItemSelect?.(item)
    },
    [onItemSelect]
  )

  // Handle navigation
  const handleNavigateBack = useCallback(() => {
    if (currentPath.length > 1) {
      const newPath = currentPath.slice(0, -1)
      setCurrentPath(newPath)
      setSelectedCategory(newPath[newPath.length - 1] || categories[0]?.id || '')
      onNavigate?.(newPath)
    }
  }, [currentPath, categories, onNavigate])

  const handleNavigateForward = useCallback(() => {
    // Forward navigation would require history tracking
    // For now, this is a placeholder
  }, [])

  // Build breadcrumb from path
  const breadcrumb = useMemo(() => {
    const crumbs: Array<{ id: string; name: string }> = [{ id: 'root', name: title }]

    for (const pathPart of currentPath) {
      const category = categories.find((c) => c.id === pathPart)
      if (category) {
        crumbs.push({ id: category.id, name: category.name })
      }
    }

    return crumbs
  }, [currentPath, categories, title])

  // Render content based on view mode
  const renderContent = () => {
    if (viewMode === 'icon') {
      return (
        <FinderIconView
          items={currentItems}
          selectedItem={selectedItem}
          onItemSelect={handleItemSelect}
        />
      )
    }

    return (
      <FinderListView
        items={currentItems}
        selectedItem={selectedItem}
        onItemSelect={handleItemSelect}
        showPreview={showPreview}
      />
    )
  }

  // Render sidebar content
  const renderSidebar = () => (
    <FinderSidebar
      categories={categories}
      selectedCategory={selectedCategory}
      onCategorySelect={handleCategorySelect}
    />
  )

  return (
    <MacStudioWindow
      windowId={windowId}
      title={title}
      size="large"
      initialPosition={initialPosition}
      className="finder-window"
    >
      <div className="finder-container">
        <FinderToolbar
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          canGoBack={currentPath.length > 0}
          canGoForward={false}
          onNavigateBack={handleNavigateBack}
          onNavigateForward={handleNavigateForward}
          breadcrumb={breadcrumb}
        />

        <FinderSplitPane
          sidebar={showSidebar ? renderSidebar() : null}
          content={renderContent()}
          storageKey={storageKey}
        />
      </div>
    </MacStudioWindow>
  )
}

export default FinderWindow
